import { defineStore } from 'pinia';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import type {
  BankAccountInfosType,
  BnplDetailType,
  CaptureTransactionBodyType,
  ParentTransactionInfosType,
  TransactionActionBodyType,
  TransactionActionType,
  TransactionCardDetailsType,
  TransactionInfosP2PType,
  TransactionInfosType,
  TransactionPSPDetailsType,
  TransactionResponseType,
  CustomStoreLoadType,
  StoreLoadOptions,
  DataSourceOption,
  BankAccountFilter,
  CustomStoreOptions,
  ChargebackBodyType,
  CheckRequestBodyType,
  DataGridRef,
  TransactionHistoryStep
} from '~/types';
import useTransactionsApi from '~/composables/api/useTransactionsApi';

const isProcessedLoad = (loadMode: CustomStoreLoadType): boolean => {
  return loadMode === 'processed';
};

const fetchTransactionData = (options: StoreLoadOptions, loadUrl: string) => {
  const params = Object.keys(options)
    .map((key: string) => {
      // @ts-ignore
      const value = options[key];
      if (value !== undefined && value !== null) {
        return `${key}=${encodeURIComponent(value.toString())}`;
      }
      return '';
    })
    .join('&');

  return useTransactionsApi()
    .getTransactionsData(loadUrl, params)
    .then((response) => {
      return {
        data: response.data,
        totalCount: response.totalCount,
        groupCount: response.totalCount
      };
    });
};

export const useTransactionsStore = defineStore('transactions', {
  state: () => ({
    sendPaymentModal: false as boolean,
    SimulateMoneyInModal: false as boolean,
    openConfirmActionModal: false as boolean,
    refundPaymentModal: false as boolean,
    captureTransactionModal: false as boolean,
    checkRequestModal: false as boolean,
    walletData: {} as { senderName: string; walletExtId: string; icon: string },
    transactionInfos: undefined as TransactionInfosType | undefined,
    transactionP2PInfos: undefined as TransactionInfosP2PType | undefined,
    transactionActions: undefined as TransactionActionType[] | undefined,
    pspDetails: undefined as TransactionPSPDetailsType | undefined,
    cardDetails: undefined as TransactionCardDetailsType | undefined,
    relatedTransactionsCount: 0,
    parentTransaction: undefined as ParentTransactionInfosType | undefined,
    relatedTransactions: undefined as TransactionResponseType[] | undefined,
    activeTab: 'details' as string,
    bnplDetails: undefined as BnplDetailType | undefined,
    confirmPassword: {
      value: '' as string | undefined,
      isValid: undefined as boolean | undefined
    },
    transactionActionBody: undefined as TransactionActionBodyType | undefined,
    transactionActionUrl: '' as string,
    bankAccountInfos: undefined as BankAccountInfosType | undefined,
    transactionAction: '' as string,
    captureTransactionBody: undefined as CaptureTransactionBodyType | undefined,
    checkRequestBody: undefined as CheckRequestBodyType | undefined,
    checkRequestForm: {
      comment: { value: '', isValid: false },
      password: { value: '', isValid: false }
    },
    paymentMethods: ['Incoming bank transfer', 'Incoming cheque'] as Array<string>,
    selectedSddMandateFilter: null as number | null,
    selectedCardFilter: null as number | null,
    selectedBankAccountFilter: null as BankAccountFilter | null,
    chargeBackModal: false as boolean,
    chargeBackPayload: undefined as ChargebackBodyType | undefined,
    transactionsDataGrid: undefined as DataGridRef | undefined,
    history: [] as TransactionHistoryStep[]
  }),

  actions: {
    handleTabChange(newTab: string) {
      this.activeTab = newTab;
    },
    handleActiveTabs(tab: string) {
      return this.activeTab === tab;
    },
    updateRelatedTransactionsCount(count: number) {
      this.relatedTransactionsCount = count;
    },
    showSendPaymentToAnotherWalletModal(walletData: {
      senderName: string;
      walletExtId: string;
      icon: string;
    }) {
      this.walletData = walletData;
      this.sendPaymentModal = true;
    },
    createTransactionStore({
      loadMode,
      loadUrl,
      keyExpr,
      skip,
      take
    }: CustomStoreOptions): CustomStore {
      return new CustomStore({
        key: keyExpr,
        load: async (loadOptions) => {
          const options: StoreLoadOptions = {
            skip: loadOptions.skip ?? skip,
            take: loadOptions.take ?? take,
            requireTotalCount: isProcessedLoad(loadMode),
            paginateViaPrimaryKey: true,
            sort: JSON.stringify(loadOptions.sort ?? [{ selector: keyExpr, desc: true }])
          };

          if (loadOptions.filter) {
            options.filter = JSON.stringify(loadOptions.filter);
          }

          try {
            const result = await fetchTransactionData(options, loadUrl);
            return result;
          } catch (error) {}
        },
        loadMode
      });
    },
    createTransactionDataSource(options: DataSourceOption): DataSource {
      const { storeOptions, filter } = options;
      const store = this.createTransactionStore(storeOptions);
      return new DataSource({ store, filter });
    },
    setSelectedSddMandateFilter(mandateId: number | null) {
      this.selectedSddMandateFilter = mandateId;
    },
    setSelectedCardFilter(cardId: number | null) {
      this.selectedCardFilter = cardId;
    },
    setSelectedBankAccountFilter(filter: BankAccountFilter | null) {
      this.selectedBankAccountFilter = filter;
    }
  }
});
